import React, { useContext, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FaPlay } from "react-icons/fa";
import DashboardContainer from "../Container";
import { TimeBookingContext } from "../../../context/TimebookingContext";
import {
    ITsBuchbaresProjekt,
    requestBuchbareProjekte,
    requestTaetigkeiten
} from "../../Timesafe/timesafeApiCalls";
import "./style.scss";

interface IOption {
    value: number;
    label: string;
}

const NO_CUSTOMER = 0;

// Menus are rendered on the body, so they are not cut off by the dashboard container
const selectProps = {
    menuPortalTarget: document.body,
    styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
    noOptionsMessage: () => "Keine Einträge"
};

function projektLabel(p: ITsBuchbaresProjekt): string {
    return [p.projektNr, p.bezeichnung].filter(Boolean).join(" ");
}

function formatStartTime(datum: string): string {
    const date = new Date(datum);
    return isNaN(date.getTime()) ? "--:--" : date.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
}

// Clock in on a TimeSafe activity: customer -> project -> activity, then play.
// Clocking out happens in the header (ActiveTimeBooking).
function ContainerTsQuickBooking(props: { classes: string }) {
    const { isActive, contextTimeBookingData, beginTimeBooking } = useContext(TimeBookingContext);

    const [projekte, setProjekte] = useState<ITsBuchbaresProjekt[]>([]);
    const [loadingProjekte, setLoadingProjekte] = useState<boolean>(true);
    const [fehler, setFehler] = useState<string>("");
    const [kunde, setKunde] = useState<IOption | null>(null);
    const [projekt, setProjekt] = useState<IOption | null>(null);
    const [taetigkeiten, setTaetigkeiten] = useState<IOption[]>([]);
    const [loadingTaetigkeiten, setLoadingTaetigkeiten] = useState<boolean>(false);
    const [taetigkeit, setTaetigkeit] = useState<IOption | null>(null);
    const [text, setText] = useState<string>("");

    // Preload all bookable projects once, the customers are derived from them
    useEffect(() => {
        requestBuchbareProjekte()
            .then((liste) => {
                const gueltig = (liste ?? []).filter(Boolean);
                setProjekte(gueltig);
                if (gueltig.length === 0) setFehler("TimeSafe liefert keine Projekte, auf die Sie buchen können.");
            })
            .catch((error) => {
                console.error(error);
                setFehler(`Projekte konnten nicht geladen werden: ${error?.message ?? error}`);
            })
            .finally(() => setLoadingProjekte(false));
    }, []);

    const kunden: IOption[] = useMemo(() => {
        const map = new Map<number, string>();
        projekte.forEach((p) => {
            const id = p.kundenID ?? NO_CUSTOMER;
            if (!map.has(id)) {
                const label = [p.kundenNr, p.kundenBezeichnung].filter(Boolean).join(" ");
                map.set(id, id === NO_CUSTOMER || !label ? "(ohne Kunde)" : label);
            }
        });
        return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    }, [projekte]);

    const projektOptionen: IOption[] = useMemo(() => {
        if (!kunde) return [];
        return projekte
            .filter((p) => (p.kundenID ?? NO_CUSTOMER) === kunde.value)
            .map((p) => ({ value: p.id, label: projektLabel(p) }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [projekte, kunde]);

    // Only one customer: select it right away
    useEffect(() => {
        if (kunden.length === 1 && !kunde) setKunde(kunden[0]);
    }, [kunden]);

    const handleKunde = (option: IOption | null) => {
        setKunde(option);
        setProjekt(null);
        setTaetigkeiten([]);
        setTaetigkeit(null);
    };

    const handleProjekt = (option: IOption | null) => {
        setProjekt(option);
        setTaetigkeiten([]);
        setTaetigkeit(null);
        setFehler("");
        if (!option) return;

        setLoadingTaetigkeiten(true);
        requestTaetigkeiten(option.value)
            .then((liste) =>
                setTaetigkeiten(
                    (liste ?? [])
                        .filter((t: any) => t && t.aktiv !== false)
                        .map((t: any) => ({ value: t.id, label: t.bezeichnungInklusiveGruppen || t.bezeichnung }))
                )
            )
            .catch((error) => {
                console.error(error);
                setFehler(`Tätigkeiten konnten nicht geladen werden: ${error?.message ?? error}`);
            })
            .finally(() => setLoadingTaetigkeiten(false));
    };

    const handleStart = () => {
        if (!projekt || !taetigkeit || isActive) return;
        beginTimeBooking(taetigkeit.value, text.trim(), `${projekt.label} – ${taetigkeit.label}`);
        setText("");
    };

    return (
        <DashboardContainer headerName="ZEITBUCHUNG" className={`ds-tsquickbooking ${props.classes}`}>
            {isActive ? (
                <div className="tsquickbooking__active">
                    <div className="tsquickbooking__active-title">Eingestempelt seit {formatStartTime(contextTimeBookingData.datum)}</div>
                    <div className="tsquickbooking__active-name">
                        {contextTimeBookingData.taetigkeitBezeichnung || `#${contextTimeBookingData.taetigkeitID}`}
                    </div>
                    <div className="tsquickbooking__hint">Ausstempeln oben im Header</div>
                </div>
            ) : (
                <div className="tsquickbooking__form">
                    {fehler && <div className="tsquickbooking__error">{fehler}</div>}
                    <Select
                        {...selectProps}
                        className="tsquickbooking__select"
                        placeholder={loadingProjekte ? "Kunden werden geladen ..." : "Kunde"}
                        isLoading={loadingProjekte}
                        isClearable
                        options={kunden}
                        value={kunde}
                        onChange={(option) => handleKunde(option as IOption | null)}
                    />
                    <Select
                        {...selectProps}
                        className="tsquickbooking__select"
                        placeholder="Projekt"
                        isDisabled={!kunde}
                        isClearable
                        options={projektOptionen}
                        value={projekt}
                        onChange={(option) => handleProjekt(option as IOption | null)}
                    />
                    <Select
                        {...selectProps}
                        className="tsquickbooking__select"
                        placeholder={loadingTaetigkeiten ? "Tätigkeiten werden geladen ..." : "Tätigkeit"}
                        isLoading={loadingTaetigkeiten}
                        isDisabled={!projekt}
                        isClearable
                        options={taetigkeiten}
                        value={taetigkeit}
                        onChange={(option) => setTaetigkeit(option as IOption | null)}
                    />
                    <div className="tsquickbooking__start">
                        <input
                            type="text"
                            placeholder="Buchungstext (optional, sonst beim Ausstempeln)"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleStart();
                            }}
                        />
                        <button
                            className="tsquickbooking__play"
                            title="Einstempeln"
                            disabled={!taetigkeit}
                            onClick={handleStart}>
                            <FaPlay />
                        </button>
                    </div>
                </div>
            )}
        </DashboardContainer>
    );
}

export default ContainerTsQuickBooking;
